<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue'
const { t } = useI18n();
const sectionRef = ref<HTMLElement | null>(null);
  let observer: IntersectionObserver | undefined; // ✅ 提前定义 observer
onBeforeUnmount(() => {
  observer?.disconnect(); // ✅ 这里就能访问到上面的 observer
});
onMounted(async () => {
  await nextTick();
  // 所有需要动画的元素
  const els =
    sectionRef.value?.querySelectorAll<HTMLElement>("[data-anim]") ?? [];
  const OBS_IN = 0.1; // 可见触发进场
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        const el = e.target as HTMLElement;
        // 进场
        if (e.intersectionRatio >= OBS_IN) {
          el.dataset.inview = "true";
        }
        // 完全离场才复原
        else if (e.intersectionRatio === 0) {
          el.dataset.inview = "false";
        }
      });
    },
    {
      rootMargin: "0px 0px -15% 0px", // 出屏 15 % 才算离场
      threshold: [0, OBS_IN],
    }
  );
  els.forEach((el) => observer.observe(el));
});
</script>

<template>
  <section ref="sectionRef" class="pb-[8.6rem] mt-[2rem] flex items-center justify-center">
    <div class="container mx-auto px-4">
      <!-- 标题区域 -->
      <div class="text-center mb-16">
        <h2 
        data-anim
        style="--d:100ms"
         class="text-[3rem] font-bold text-main-primary  mb-4">
          {{ t("UseCasesLayout.digitalHumanApplicationScenarios") }}
        </h2>
        <p 
        data-anim
        style="--d:150ms"
         class="text-[1.25rem] ">
          {{ t("UseCasesLayout.realHumanAppearanceScenes") }}
        </p>
      </div>

      <!-- 卡片网格：4 列 -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        <!-- 第1个卡片 -->
        <div
         data-anim
        style="--d:200ms"
          class="bg-white rounded-xl shadow-lg overflow-hidden group hover:shadow-xl transition-all duration-300"
        >
          <div class="aspect-video relative overflow-hidden">
            <img
              src="https://oss.iiii.com/web/welcome/short_video_acquisition.png"
              :alt="t('UseCasesLayout.shortVideoAcquisition')"
              class="w-full h-full object-cover transform group-hover:scale-110 transition-all duration-500"
            />
            <div
              class="absolute inset-0 bg-gradient-to-t from-[--el-color-primary] to-transparent opacity-75"
            ></div>
          </div>
          <div class="p-6">
            <h3 class="text-xl font-bold text-gray-900 mb-3">
              {{ t("UseCasesLayout.shortVideoAcquisition") }}
            </h3>
            <p class="text-gray-600 leading-[1.3rem]">
              {{ t("UseCasesLayout.noProfessionalEquipment") }}
            </p>
          </div>
        </div>

        <!-- 第2个卡片 -->
        <div
        data-anim
        style="--d:300ms"
          class="bg-white rounded-xl shadow-lg overflow-hidden group hover:shadow-xl transition-all duration-300"
        >
          <div class="aspect-video relative overflow-hidden">
            <img
              src="https://oss.iiii.com/web/welcome/personalI_pcreation.png"
              :alt="t('UseCasesLayout.personalIPCreation')"
              class="w-full h-full object-cover transform group-hover:scale-110 transition-all duration-500"
            />
            <div
              class="absolute inset-0 bg-gradient-to-t from-[--el-color-primary] to-transparent opacity-75"
            ></div>
          </div>
          <div class="p-6">
            <h3 class="text-xl font-bold text-gray-900 mb-3">
              {{ t("UseCasesLayout.personalIPCreation") }}
            </h3>
            <p class="text-gray-600 leading-[1.3rem]">
              {{ t("UseCasesLayout.breakTimeAndEffortLimits") }}
            </p>
          </div>
        </div>

        <!-- 第3个卡片 -->
        <div
        data-anim
        style="--d:400ms"
          class="bg-white rounded-xl shadow-lg overflow-hidden group hover:shadow-xl transition-all duration-300"
        >
          <div class="aspect-video relative overflow-hidden">
            <img
              src="https://oss.iiii.com/web/welcome/brand_promotion.png"
              :alt="t('UseCasesLayout.brandPromotion')"
              class="w-full h-full object-cover transform group-hover:scale-110 transition-all duration-500"
            />
            <div
              class="absolute inset-0 bg-gradient-to-t from-[--el-color-primary] to-transparent opacity-75"
            ></div>
          </div>
          <div class="p-6">
            <h3 class="text-xl font-bold text-gray-900 mb-3">
              {{ t("UseCasesLayout.brandPromotion") }}
            </h3>
            <p class="text-gray-600 leading-[1.3rem]">
              {{ t("UseCasesLayout.customDigitalSpokesperson") }}
            </p>
          </div>
        </div>
        <!-- 第4个卡片 -->
        <div
        data-anim
        style="--d:500ms"
          class="bg-white rounded-xl shadow-lg overflow-hidden group hover:shadow-xl transition-all duration-300"
        >
          <div class="aspect-video relative overflow-hidden">
            <img
              src="https://oss.iiii.com/web/welcome/course_production.png"
              :alt="t('UseCasesLayout.courseProduction')"
              class="w-full h-full object-cover transform group-hover:scale-110 transition-all duration-500"
            />
            <div
              class="absolute inset-0 bg-gradient-to-t from-[--el-color-primary] to-transparent opacity-75"
            ></div>
          </div>
          <div class="p-6">
            <h3 class="text-xl font-bold text-gray-900 mb-3">
              {{ t("UseCasesLayout.courseProduction") }}
            </h3>
            <p class="text-gray-600 leading-[1.3rem]">
              {{ t("UseCasesLayout.quicklyTransformCourseContent") }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.partner-card:hover {
  transform: scale(1.05);
}
[data-anim] {
  --d: 0ms;                                
  opacity: 0;
  transform: translateY(40px);
  transition:
    opacity 0.7s cubic-bezier(.25,.8,.25,1) var(--d),
    transform 0.7s cubic-bezier(.25,.8,.25,1) var(--d);
  will-change: opacity, transform;
}
[data-anim][data-inview="true"] {
  opacity: 1;
  transform: translateY(0);
}
@media (prefers-reduced-motion: reduce) {
  [data-anim] {
    transition: none !important;
    opacity: 1 !important;
    transform: none !important;
  }
}
</style>
